import React from 'react';
import { cn } from '../../utils/cn';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  children: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, children, disabled, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
      primary: 'bg-accent-600 text-white hover:bg-accent-700 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] active:shadow-md active:translate-y-[1px] focus-visible:ring-accent-500',
      secondary: 'bg-white dark:bg-base-800 text-base-700 dark:text-base-300 border border-base-200 dark:border-base-700 hover:bg-base-50 dark:hover:bg-base-700 hover:border-base-300 dark:hover:border-base-600 hover:shadow-sm hover:-translate-y-0.5 active:scale-[0.98] active:shadow-xs active:translate-y-[1px] focus-visible:ring-base-400',
      ghost: 'text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100 active:scale-[0.98] active:bg-base-200 dark:active:bg-base-700 focus-visible:ring-base-400',
      danger: 'bg-red-600 text-white hover:bg-red-700 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] active:shadow-md active:translate-y-[1px] focus-visible:ring-red-500',
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-body-sm gap-1.5',
      md: 'px-5 py-2.5 text-body-md gap-2',
      lg: 'px-7 py-3.5 text-body-lg gap-2.5',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="3"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';